import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Upload, Wand2, AlertTriangle, Search, Trash2, Edit, Download,
  ArrowRightLeft, GitMerge, HardDrive, FileText, FileWarning, PackageX, ClipboardCheck,
} from "lucide-react";
import { toast } from "sonner";

import { logoColor as LOGO_URL } from "@/lib/logos";

const TIPOS_EQUIPO = [
  "Sillón dental",
  "Compresor",
  "Autoclave",
  "Rayos X",
  "Instrumental rotatorio",
  "Otro",
];

const UBI_TIPOS = [
  { value: "box", label: "Box" },
  { value: "piso", label: "Piso" },
  { value: "sala", label: "Sala" },
];

const BULK_HEADERS = [
  "cliente_nombre",
  "sucursal",
  "tipo_equipo",
  "marca",
  "modelo",
  "numero_serie",
  "codigo_interno",
  "ubicacion_tipo",
  "ubicacion_valor",
  "frecuencia_mp_meses",
  "observaciones",
];

const emptyForm = () => ({
  cliente_id: "",
  sucursal: "",
  tipo_equipo: "",
  marca: "",
  modelo: "",
  numero_serie: "",
  codigo_interno: "",
  ubicacion: { tipo: "box", valor: "" },
  frecuencia_mp_meses: 6,
  observaciones: "",
  estado: "activo",
});

function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function csvEscape(s) {
  if (s == null) return "";
  const str = String(s);
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

function parseCsv(text) {
  // Minimal CSV parser supporting quoted fields and escaped quotes.
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += c;
      }
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ",") { row.push(field); field = ""; }
      else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
      else if (c === "\r") { /* skip */ }
      else field += c;
    }
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((x) => x && x.trim() !== ""));
}

function pendienteBadge(motivo) {
  if (motivo === "vencida") return <Badge className="bg-red-100 text-red-700 border-0">Vencida</Badge>;
  if (motivo === "proxima") return <Badge className="bg-amber-100 text-amber-700 border-0">Próxima</Badge>;
  if (motivo === "nunca") return <Badge className="bg-gray-100 text-gray-600 border-0">Nunca</Badge>;
  return null;
}

export default function MisEquipos() {
  const { api } = useAuth();
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [filters, setFilters] = useState({ cliente_id: "", sucursal: "", estado: "", q: "", tipo_equipo: "" });
  const [search, setSearch] = useState("");
  const qDebounced = useDebounced(search, 300);

  const [tab, setTab] = useState("todos");

  const [equipos, setEquipos] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [diasMargen, setDiasMargen] = useState(30);

  const [selected, setSelected] = useState([]); // ids
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailEditing, setDetailEditing] = useState(false);
  const [detailForm, setDetailForm] = useState(emptyForm());
  const [savingDetail, setSavingDetail] = useState(false);

  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState(emptyForm());
  const [savingNew, setSavingNew] = useState(false);

  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const fileRef = useRef(null);

  const [showTransfer, setShowTransfer] = useState(false);
  const [transferForm, setTransferForm] = useState({ nueva_sucursal: "", nuevo_cliente_id: "", motivo: "" });

  const [showMerge, setShowMerge] = useState(false);
  const [mergeTarget, setMergeTarget] = useState("");

  const [showMergeDir, setShowMergeDir] = useState(false);
  const [mergeDirForm, setMergeDirForm] = useState({ sucursal_origen: "", sucursal_destino: "" });

  // load clients on mount
  useEffect(() => {
    api.get("/api/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [api]);

  // refresh filters' q from debounced search
  useEffect(() => {
    setFilters((p) => ({ ...p, q: qDebounced }));
  }, [qDebounced]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === filters.cliente_id),
    [clients, filters.cliente_id]
  );

  const sucursales = selectedClient?.direcciones || [];

  const load = async () => {
    setLoading(true);
    try {
      if (tab === "pendientes") {
        const r = await api.get(`/api/equipos/pendientes-mp?dias_margen=${diasMargen}`);
        setPendientes(r.data || []);
      } else {
        const params = new URLSearchParams();
        if (filters.cliente_id) params.set("cliente_id", filters.cliente_id);
        if (filters.sucursal) params.set("sucursal", filters.sucursal);
        if (filters.estado && filters.estado !== "todos") params.set("estado", filters.estado);
        if (filters.tipo_equipo && filters.tipo_equipo !== "todos") params.set("tipo_equipo", filters.tipo_equipo);
        if (filters.q) params.set("q", filters.q);
        const r = await api.get(`/api/equipos?${params.toString()}`);
        setEquipos(r.data || []);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar equipos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [
    filters.cliente_id, filters.sucursal, filters.estado, filters.tipo_equipo, filters.q, tab, diasMargen,
  ]);

  // when cliente changes, reset sucursal
  useEffect(() => {
    setFilters((p) => ({ ...p, sucursal: "" }));
  }, [filters.cliente_id]);

  const toggleSel = (id) => {
    setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : (p.length >= 2 ? [p[1], id] : [...p, id]));
  };

  const openDetail = async (eq) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailEditing(false);
    try {
      const r = await api.get(`/api/equipos/${eq.id}`);
      setDetail(r.data);
      setDetailForm({
        cliente_id: r.data.cliente_id || "",
        sucursal: r.data.sucursal || "",
        tipo_equipo: r.data.tipo_equipo || "",
        marca: r.data.marca || "",
        modelo: r.data.modelo || "",
        numero_serie: r.data.numero_serie || "",
        codigo_interno: r.data.codigo_interno || "",
        ubicacion: r.data.ubicacion || { tipo: "box", valor: "" },
        frecuencia_mp_meses: r.data.frecuencia_mp_meses ?? 6,
        observaciones: r.data.observaciones || "",
        estado: r.data.estado || "activo",
      });
    } catch (e) {
      toast.error("Error al cargar el equipo");
      setDetailOpen(false);
    }
  };

  const saveDetail = async () => {
    if (!detail) return;
    setSavingDetail(true);
    try {
      await api.put(`/api/equipos/${detail.id}`, detailForm);
      toast.success("Equipo actualizado");
      setDetailEditing(false);
      const r = await api.get(`/api/equipos/${detail.id}`);
      setDetail(r.data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSavingDetail(false); }
  };

  const removeEquipo = async () => {
    if (!detail) return;
    if (!window.confirm(`¿Eliminar el equipo ${detail.marca || ""} ${detail.modelo || ""} (${detail.numero_serie || "s/n"})?`)) return;
    try {
      await api.delete(`/api/equipos/${detail.id}`);
      toast.success("Equipo eliminado");
      setDetailOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); }
  };

  const createNew = async () => {
    if (!newForm.cliente_id) { toast.error("Selecciona un cliente"); return; }
    if (!newForm.sucursal) { toast.error("Selecciona una sucursal"); return; }
    if (!newForm.tipo_equipo) { toast.error("Indica el tipo de equipo"); return; }
    setSavingNew(true);
    try {
      await api.post("/api/equipos", newForm);
      toast.success("Equipo creado");
      setShowNew(false);
      setNewForm(emptyForm());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al crear equipo"); }
    finally { setSavingNew(false); }
  };

  const downloadTemplate = () => {
    const sample = [
      BULK_HEADERS.join(","),
      [
        "Clínica Ejemplo S.A.",
        "Sucursal Centro",
        "Sillón dental",
        "Gnatus",
        "S300",
        "ABC123",
        "EQ-001",
        "box",
        "Box 1",
        "6",
        "Equipo principal sala 1",
      ].map(csvEscape).join(","),
    ].join("\n");
    const blob = new Blob([sample], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "equipos_plantilla.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setBulkText(String(reader.result || ""));
    reader.readAsText(f);
  };

  const submitBulk = async () => {
    const txt = bulkText.trim();
    if (!txt) { toast.error("Pega el CSV o JSON o sube un archivo"); return; }
    let rows;
    try {
      if (txt.startsWith("[")) {
        rows = JSON.parse(txt);
      } else {
        const matrix = parseCsv(txt);
        if (matrix.length < 2) throw new Error("CSV vacío");
        const headers = matrix[0].map((h) => h.trim());
        rows = matrix.slice(1).map((cols) => {
          const obj = {};
          headers.forEach((h, idx) => { obj[h] = (cols[idx] ?? "").trim(); });
          if (obj.frecuencia_mp_meses) obj.frecuencia_mp_meses = parseInt(obj.frecuencia_mp_meses, 10) || undefined;
          return obj;
        });
      }
    } catch (e) {
      toast.error(`Error al parsear: ${e.message}`);
      return;
    }
    setBulkBusy(true);
    try {
      const r = await api.post("/api/equipos/bulk", rows);
      const { creados = 0, actualizados = 0, errores = 0 } = r.data || {};
      toast.success(`Creados: ${creados} · Actualizados: ${actualizados} · Errores: ${errores}`);
      setShowBulk(false);
      setBulkText("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error en carga masiva"); }
    finally { setBulkBusy(false); }
  };

  const submitTransfer = async () => {
    if (!detail) return;
    if (!transferForm.nueva_sucursal) { toast.error("Indica la nueva sucursal"); return; }
    try {
      await api.post(`/api/equipos/${detail.id}/transfer`, {
        nueva_sucursal: transferForm.nueva_sucursal,
        nuevo_cliente_id: transferForm.nuevo_cliente_id || undefined,
        motivo: transferForm.motivo || "",
      });
      toast.success("Equipo trasladado");
      setShowTransfer(false);
      setTransferForm({ nueva_sucursal: "", nuevo_cliente_id: "", motivo: "" });
      setDetailOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al trasladar"); }
  };

  const submitMerge = async () => {
    if (selected.length !== 2) return;
    if (!mergeTarget) { toast.error("Elige cuál es el equipo destino"); return; }
    const source = selected.find((x) => x !== mergeTarget);
    if (!window.confirm("¿Fusionar? El equipo origen será eliminado y su historial quedará en el destino.")) return;
    try {
      await api.post("/api/equipos/merge", { target_id: mergeTarget, source_id: source });
      toast.success("Equipos fusionados");
      setShowMerge(false);
      setSelected([]);
      setMergeTarget("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al fusionar"); }
  };

  const submitMergeDirecciones = async () => {
    if (!filters.cliente_id) { toast.error("Elige un cliente primero"); return; }
    if (!mergeDirForm.sucursal_origen || !mergeDirForm.sucursal_destino) { toast.error("Indica origen y destino"); return; }
    if (mergeDirForm.sucursal_origen === mergeDirForm.sucursal_destino) { toast.error("Origen y destino deben ser distintos"); return; }
    try {
      await api.post("/api/equipos/merge-direcciones", {
        cliente_id: filters.cliente_id,
        sucursal_destino: mergeDirForm.sucursal_destino,
        sucursal_origen: mergeDirForm.sucursal_origen,
      });
      toast.success("Direcciones fusionadas");
      setShowMergeDir(false);
      setMergeDirForm({ sucursal_origen: "", sucursal_destino: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al fusionar direcciones"); }
  };

  const poblarHistorial = async () => {
    if (!window.confirm("¿Poblar el catálogo de equipos desde el historial existente (MP/MC/IT/Bajas)?")) return;
    try {
      const r = await api.post("/api/equipos/from-history");
      const count = r.data?.creados ?? r.data?.count ?? 0;
      toast.success(`Equipos creados desde historial: ${count}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al poblar"); }
  };

  const uppercaseAll = async () => {
    if (!window.confirm("¿Convertir TODOS los datos maestros (clientes, direcciones, equipos, marcas, modelos, series, etc.) a MAYÚSCULAS? Esta operación es idempotente.")) return;
    try {
      const r = await api.post("/api/admin/uppercase-all");
      const total = r.data?.total_documentos_tocados ?? 0;
      toast.success(`Conversión completa: ${total} documentos actualizados`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error en la conversión"); }
  };

  const downloadBlob = async (url, filename) => {
    try {
      const r = await api.get(url, { responseType: "blob" });
      const obj = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = obj; a.download = filename; a.click();
      window.URL.revokeObjectURL(obj);
    } catch { toast.error("Error al descargar PDF"); }
  };

  const fmtUbi = (u) => {
    if (!u) return "—";
    if (typeof u === "string") return u;
    return u.valor ? `${u.tipo || ""} ${u.valor}`.trim() : (u.tipo || "—");
  };

  const fmtDate = (d) => d ? String(d).slice(0, 10) : "—";

  const rows = tab === "pendientes" ? pendientes : equipos;

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <img src={LOGO_URL} alt="" className="h-7 hidden sm:block" />
            <div className="min-w-0">
              <h1 className="text-sm font-semibold text-[#384091] truncate">Mis Equipos</h1>
              <p className="text-[11px] text-gray-500 truncate">Catálogo central por cliente y sucursal</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {selected.length === 2 && (
              <Button size="sm" variant="outline" className="border-[#384091] text-[#384091]" onClick={() => { setMergeTarget(selected[0]); setShowMerge(true); }}>
                <GitMerge className="h-3.5 w-3.5 mr-1" /> Fusionar 2 seleccionados
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/dashboard")} title="Volver">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        {/* Toolbar */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Cliente</Label>
                <Select value={filters.cliente_id || "todos"} onValueChange={(v) => setFilters((p) => ({ ...p, cliente_id: v === "todos" ? "" : v }))}>
                  <SelectTrigger className="h-10 text-xs bg-white"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    <SelectItem value="todos">Todos</SelectItem>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Sucursal</Label>
                <div className="flex items-center gap-1">
                  <Select value={filters.sucursal || "todas"} onValueChange={(v) => setFilters((p) => ({ ...p, sucursal: v === "todas" ? "" : v }))} disabled={!filters.cliente_id}>
                    <SelectTrigger className="h-10 text-xs bg-white"><SelectValue placeholder={filters.cliente_id ? "Todas" : "Elige un cliente"} /></SelectTrigger>
                    <SelectContent className="bg-white max-h-72">
                      <SelectItem value="todas">Todas</SelectItem>
                      {sucursales.map((d) => <SelectItem key={d.nombre} value={d.nombre}>{d.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {filters.cliente_id && (
                    <Button variant="outline" size="sm" className="h-10 px-2" title="Fusionar sucursales" onClick={() => setShowMergeDir(true)}>
                      <GitMerge className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-xs">Estado</Label>
                <Select value={filters.estado || "todos"} onValueChange={(v) => setFilters((p) => ({ ...p, estado: v === "todos" ? "" : v }))}>
                  <SelectTrigger className="h-10 text-xs bg-white"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="activo">Activo</SelectItem>
                    <SelectItem value="de_baja">De baja</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Búsqueda</Label>
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-2 top-3 text-gray-400" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} className="h-10 text-xs pl-7 bg-white" placeholder="Marca, modelo, serie, código, box…" />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" className="bg-[#0FB4B7] hover:bg-[#0D999C] text-white" onClick={() => { setNewForm(emptyForm()); setShowNew(true); }}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Nuevo equipo
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowBulk(true)}>
                <Upload className="h-3.5 w-3.5 mr-1" /> Carga masiva
              </Button>
              <Button size="sm" variant="outline" className="border-[#534AB7] text-[#534AB7]" onClick={poblarHistorial}>
                <Wand2 className="h-3.5 w-3.5 mr-1" /> Poblar desde historial
              </Button>
              <Button size="sm" variant="outline" className="border-gray-500 text-gray-700" onClick={uppercaseAll}>
                <Wand2 className="h-3.5 w-3.5 mr-1" /> Convertir a MAYÚSCULAS
              </Button>
              <Button size="sm" variant="outline" className="border-amber-500 text-amber-700" onClick={() => setTab("pendientes")}>
                <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Pendientes MP
              </Button>
            </div>
          </CardContent>
        </Card>

        <Tabs value={tab} onValueChange={(v) => { setTab(v); setSelected([]); }}>
          <TabsList>
            <TabsTrigger value="todos">Todos</TabsTrigger>
            <TabsTrigger value="pendientes">Pendientes MP</TabsTrigger>
          </TabsList>

          <TabsContent value="todos" className="mt-3">
            <EquiposTable
              rows={rows}
              loading={loading}
              selected={selected}
              toggleSel={toggleSel}
              openDetail={openDetail}
              fmtUbi={fmtUbi}
              fmtDate={fmtDate}
              showPending={false}
            />
          </TabsContent>

          <TabsContent value="pendientes" className="mt-3">
            <div className="mb-2 flex items-center gap-2">
              <Label className="text-xs">Margen (días):</Label>
              <Input type="number" value={diasMargen} onChange={(e) => setDiasMargen(parseInt(e.target.value || "0", 10))} className="h-8 w-20 text-xs bg-white" />
            </div>
            <EquiposTable
              rows={rows}
              loading={loading}
              selected={selected}
              toggleSel={toggleSel}
              openDetail={openDetail}
              fmtUbi={fmtUbi}
              fmtDate={fmtDate}
              showPending={true}
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* ====== Detail Dialog ====== */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-[#384091] flex items-center gap-2">
              <HardDrive className="h-4 w-4" />
              {detail ? `${detail.marca || ""} ${detail.modelo || ""}`.trim() || "Equipo" : "Cargando…"}
            </DialogTitle>
          </DialogHeader>
          {!detail ? (
            <p className="text-center text-gray-400 py-8">Cargando…</p>
          ) : (
            <div className="space-y-4">
              {/* Identificación */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Cliente</Label>
                  {detailEditing ? (
                    <Select value={detailForm.cliente_id} onValueChange={(v) => setDetailForm((p) => ({ ...p, cliente_id: v, sucursal: "" }))}>
                      <SelectTrigger className="h-9 text-xs bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white max-h-72">
                        {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : <p className="text-sm">{detail.cliente_nombre || "—"}</p>}
                </div>
                <div>
                  <Label className="text-xs">Sucursal</Label>
                  {detailEditing ? (
                    <Select value={detailForm.sucursal} onValueChange={(v) => setDetailForm((p) => ({ ...p, sucursal: v }))}>
                      <SelectTrigger className="h-9 text-xs bg-white"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                      <SelectContent className="bg-white max-h-72">
                        {(clients.find((c) => c.id === detailForm.cliente_id)?.direcciones || []).map((d) => (
                          <SelectItem key={d.nombre} value={d.nombre}>{d.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : <p className="text-sm">{detail.sucursal || "—"}</p>}
                </div>
                <div>
                  <Label className="text-xs">Tipo de equipo</Label>
                  {detailEditing ? (
                    <Input value={detailForm.tipo_equipo} onChange={(e) => setDetailForm((p) => ({ ...p, tipo_equipo: e.target.value }))} className="h-9 text-xs" />
                  ) : <p className="text-sm">{detail.tipo_equipo || "—"}</p>}
                </div>
                <div>
                  <Label className="text-xs">Estado</Label>
                  {detailEditing ? (
                    <Select value={detailForm.estado} onValueChange={(v) => setDetailForm((p) => ({ ...p, estado: v }))}>
                      <SelectTrigger className="h-9 text-xs bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="activo">Activo</SelectItem>
                        <SelectItem value="de_baja">De baja</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge className={detail.estado === "de_baja" ? "bg-gray-100 text-gray-600 border-0" : "bg-emerald-50 text-emerald-700 border-0"}>
                      {detail.estado === "de_baja" ? "De baja" : "Activo"}
                    </Badge>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Marca</Label>
                  {detailEditing ? (
                    <Input value={detailForm.marca} onChange={(e) => setDetailForm((p) => ({ ...p, marca: e.target.value }))} className="h-9 text-xs" />
                  ) : <p className="text-sm">{detail.marca || "—"}</p>}
                </div>
                <div>
                  <Label className="text-xs">Modelo</Label>
                  {detailEditing ? (
                    <Input value={detailForm.modelo} onChange={(e) => setDetailForm((p) => ({ ...p, modelo: e.target.value }))} className="h-9 text-xs" />
                  ) : <p className="text-sm">{detail.modelo || "—"}</p>}
                </div>
                <div>
                  <Label className="text-xs">Número de serie</Label>
                  {detailEditing ? (
                    <Input value={detailForm.numero_serie} onChange={(e) => setDetailForm((p) => ({ ...p, numero_serie: e.target.value }))} className="h-9 text-xs" />
                  ) : <p className="text-sm">{detail.numero_serie || "—"}</p>}
                </div>
                <div>
                  <Label className="text-xs">Código interno</Label>
                  {detailEditing ? (
                    <Input value={detailForm.codigo_interno} onChange={(e) => setDetailForm((p) => ({ ...p, codigo_interno: e.target.value }))} className="h-9 text-xs" />
                  ) : <p className="text-sm">{detail.codigo_interno || "—"}</p>}
                </div>
                <div>
                  <Label className="text-xs">Ubicación</Label>
                  {detailEditing ? (
                    <div className="flex gap-2">
                      <Select value={detailForm.ubicacion?.tipo || "box"} onValueChange={(v) => setDetailForm((p) => ({ ...p, ubicacion: { ...(p.ubicacion || {}), tipo: v } }))}>
                        <SelectTrigger className="h-9 text-xs bg-white w-24"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {UBI_TIPOS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input value={detailForm.ubicacion?.valor || ""} onChange={(e) => setDetailForm((p) => ({ ...p, ubicacion: { ...(p.ubicacion || {}), valor: e.target.value } }))} className="h-9 text-xs flex-1" placeholder="Ej: Box 2" />
                    </div>
                  ) : <p className="text-sm">{fmtUbi(detail.ubicacion)}</p>}
                </div>
                <div>
                  <Label className="text-xs">Frecuencia MP (meses)</Label>
                  {detailEditing ? (
                    <Input type="number" value={detailForm.frecuencia_mp_meses} onChange={(e) => setDetailForm((p) => ({ ...p, frecuencia_mp_meses: parseInt(e.target.value || "0", 10) }))} className="h-9 text-xs" />
                  ) : <p className="text-sm">{detail.frecuencia_mp_meses ?? "—"}</p>}
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Observaciones</Label>
                  {detailEditing ? (
                    <Textarea value={detailForm.observaciones} onChange={(e) => setDetailForm((p) => ({ ...p, observaciones: e.target.value }))} className="text-xs" rows={2} />
                  ) : <p className="text-sm whitespace-pre-wrap">{detail.observaciones || "—"}</p>}
                </div>
                <div className="sm:col-span-2 grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Última MP</Label><p className="text-sm">{fmtDate(detail.ultima_mp_fecha)}</p></div>
                  <div><Label className="text-xs">Próxima MP</Label><p className="text-sm">{fmtDate(detail.proxima_mp_fecha)}</p></div>
                </div>
              </div>

              {/* Historial técnico */}
              <div className="border-t pt-3">
                <h3 className="font-semibold text-[#384091] text-sm mb-2">Historial técnico</h3>

                <HistorialSection
                  title="Mantenciones"
                  icon={ClipboardCheck}
                  items={detail.mantenciones || []}
                  onDownload={(it) => downloadBlob(`/api/maintenance/${it.id}/pdf`, `${it.folio || it.id}.pdf`)}
                />
                <HistorialSection
                  title="Informes técnicos"
                  icon={FileWarning}
                  items={detail.informes || []}
                  onDownload={(it) => downloadBlob(`/api/informes/${it.id}/pdf`, `${it.folio || it.id}.pdf`)}
                />
                <HistorialSection
                  title="Bajas"
                  icon={PackageX}
                  items={detail.bajas || []}
                  onDownload={(it) => downloadBlob(`/api/bajas/${it.id}/pdf`, `${it.folio || it.id}.pdf`)}
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            {detail && !detailEditing && (
              <>
                <Button variant="outline" onClick={() => { setTransferForm({ nueva_sucursal: "", nuevo_cliente_id: "", motivo: "" }); setShowTransfer(true); }}>
                  <ArrowRightLeft className="h-3.5 w-3.5 mr-1" /> Trasladar
                </Button>
                <Button className="bg-[#384091] text-white" onClick={() => setDetailEditing(true)}>
                  <Edit className="h-3.5 w-3.5 mr-1" /> Editar
                </Button>
                <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={removeEquipo}>
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Eliminar
                </Button>
              </>
            )}
            {detail && detailEditing && (
              <>
                <Button variant="outline" onClick={() => setDetailEditing(false)}>Cancelar</Button>
                <Button className="bg-[#0FB4B7] text-white" onClick={saveDetail} disabled={savingDetail}>
                  {savingDetail ? "Guardando…" : "Guardar"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ====== Nuevo equipo ====== */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader><DialogTitle className="text-[#384091]">Nuevo equipo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Cliente *</Label>
              <Select value={newForm.cliente_id} onValueChange={(v) => setNewForm((p) => ({ ...p, cliente_id: v, sucursal: "" }))}>
                <SelectTrigger className="h-9 text-xs bg-white"><SelectValue placeholder="Seleccionar…" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Sucursal *</Label>
              <Select value={newForm.sucursal} onValueChange={(v) => setNewForm((p) => ({ ...p, sucursal: v }))} disabled={!newForm.cliente_id}>
                <SelectTrigger className="h-9 text-xs bg-white"><SelectValue placeholder={newForm.cliente_id ? "Seleccionar…" : "Elige cliente primero"} /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {(clients.find((c) => c.id === newForm.cliente_id)?.direcciones || []).map((d) => (
                    <SelectItem key={d.nombre} value={d.nombre}>{d.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tipo de equipo *</Label>
              <div className="flex gap-2">
                <Select value={TIPOS_EQUIPO.includes(newForm.tipo_equipo) ? newForm.tipo_equipo : ""} onValueChange={(v) => setNewForm((p) => ({ ...p, tipo_equipo: v }))}>
                  <SelectTrigger className="h-9 text-xs bg-white w-44"><SelectValue placeholder="Lista" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {TIPOS_EQUIPO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input value={newForm.tipo_equipo} onChange={(e) => setNewForm((p) => ({ ...p, tipo_equipo: e.target.value }))} placeholder="o escribe…" className="h-9 text-xs flex-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Marca</Label><Input className="h-9 text-xs" value={newForm.marca} onChange={(e) => setNewForm((p) => ({ ...p, marca: e.target.value }))} /></div>
              <div><Label className="text-xs">Modelo</Label><Input className="h-9 text-xs" value={newForm.modelo} onChange={(e) => setNewForm((p) => ({ ...p, modelo: e.target.value }))} /></div>
              <div><Label className="text-xs">N° de serie</Label><Input className="h-9 text-xs" value={newForm.numero_serie} onChange={(e) => setNewForm((p) => ({ ...p, numero_serie: e.target.value }))} /></div>
              <div><Label className="text-xs">Código interno</Label><Input className="h-9 text-xs" value={newForm.codigo_interno} onChange={(e) => setNewForm((p) => ({ ...p, codigo_interno: e.target.value }))} /></div>
            </div>
            <div>
              <Label className="text-xs">Ubicación</Label>
              <div className="flex items-center gap-3">
                <div className="flex gap-2">
                  {UBI_TIPOS.map((u) => (
                    <label key={u.value} className="flex items-center gap-1 text-xs">
                      <input type="radio" name="ubi-tipo" checked={newForm.ubicacion.tipo === u.value} onChange={() => setNewForm((p) => ({ ...p, ubicacion: { ...p.ubicacion, tipo: u.value } }))} />
                      {u.label}
                    </label>
                  ))}
                </div>
                <Input className="h-9 text-xs flex-1" placeholder="Ej: Box 1" value={newForm.ubicacion.valor} onChange={(e) => setNewForm((p) => ({ ...p, ubicacion: { ...p.ubicacion, valor: e.target.value } }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Frecuencia MP (meses)</Label>
                <Input type="number" className="h-9 text-xs" value={newForm.frecuencia_mp_meses} onChange={(e) => setNewForm((p) => ({ ...p, frecuencia_mp_meses: parseInt(e.target.value || "0", 10) }))} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Observaciones</Label>
              <Textarea value={newForm.observaciones} onChange={(e) => setNewForm((p) => ({ ...p, observaciones: e.target.value }))} rows={2} className="text-xs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
            <Button className="bg-[#0FB4B7] text-white" onClick={createNew} disabled={savingNew}>
              {savingNew ? "Creando…" : "Crear equipo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ====== Carga masiva ====== */}
      <Dialog open={showBulk} onOpenChange={setShowBulk}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader><DialogTitle className="text-[#384091]">Carga masiva de equipos</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              Pega CSV (con cabecera) o un array JSON con los campos: <code>{BULK_HEADERS.join(", ")}</code>.
              Usa <code>cliente_id</code> o <code>cliente_nombre</code>.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={downloadTemplate}>
                <Download className="h-3.5 w-3.5 mr-1" /> Descargar plantilla CSV
              </Button>
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                <Upload className="h-3.5 w-3.5 mr-1" /> Subir archivo
              </Button>
              <input type="file" accept=".csv,.json,text/csv,application/json" ref={fileRef} onChange={onPickFile} className="hidden" />
            </div>
            <Textarea rows={10} value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder="Pega aquí el CSV o JSON…" className="text-xs font-mono" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulk(false)}>Cancelar</Button>
            <Button className="bg-[#0FB4B7] text-white" onClick={submitBulk} disabled={bulkBusy}>
              {bulkBusy ? "Procesando…" : "Procesar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ====== Trasladar ====== */}
      <Dialog open={showTransfer} onOpenChange={setShowTransfer}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader><DialogTitle className="text-[#384091]">Trasladar equipo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nuevo cliente (opcional)</Label>
              <Select value={transferForm.nuevo_cliente_id || "mismo"} onValueChange={(v) => setTransferForm((p) => ({ ...p, nuevo_cliente_id: v === "mismo" ? "" : v, nueva_sucursal: "" }))}>
                <SelectTrigger className="h-9 text-xs bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  <SelectItem value="mismo">Mismo cliente</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Nueva sucursal *</Label>
              <Select value={transferForm.nueva_sucursal} onValueChange={(v) => setTransferForm((p) => ({ ...p, nueva_sucursal: v }))}>
                <SelectTrigger className="h-9 text-xs bg-white"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {(clients.find((c) => c.id === (transferForm.nuevo_cliente_id || detail?.cliente_id))?.direcciones || []).map((d) => (
                    <SelectItem key={d.nombre} value={d.nombre}>{d.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Motivo</Label>
              <Textarea rows={3} value={transferForm.motivo} onChange={(e) => setTransferForm((p) => ({ ...p, motivo: e.target.value }))} className="text-xs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransfer(false)}>Cancelar</Button>
            <Button className="bg-[#0FB4B7] text-white" onClick={submitTransfer}>Trasladar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ====== Fusionar 2 equipos ====== */}
      <Dialog open={showMerge} onOpenChange={setShowMerge}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader><DialogTitle className="text-[#384091]">Fusionar equipos</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-gray-500">Selecciona cuál de los dos equipos quedará como el destino. El otro será eliminado y su historial se traspasará.</p>
            {selected.map((id) => {
              const eq = equipos.find((x) => x.id === id);
              if (!eq) return null;
              return (
                <label key={id} className="flex items-center gap-2 border rounded-md p-2 cursor-pointer">
                  <input type="radio" name="merge-target" checked={mergeTarget === id} onChange={() => setMergeTarget(id)} />
                  <div>
                    <p className="text-xs font-medium">{eq.marca} {eq.modelo} · {eq.numero_serie || "s/n"}</p>
                    <p className="text-[10px] text-gray-500">{eq.cliente_nombre} · {eq.sucursal} · {fmtUbi(eq.ubicacion)}</p>
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMerge(false)}>Cancelar</Button>
            <Button className="bg-[#384091] text-white" onClick={submitMerge}>Fusionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ====== Fusionar direcciones ====== */}
      <Dialog open={showMergeDir} onOpenChange={setShowMergeDir}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader><DialogTitle className="text-[#384091]">Fusionar sucursales</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-gray-500">Mueve los equipos de la sucursal de origen a la de destino para el cliente seleccionado.</p>
            <div>
              <Label className="text-xs">Sucursal origen</Label>
              <Select value={mergeDirForm.sucursal_origen} onValueChange={(v) => setMergeDirForm((p) => ({ ...p, sucursal_origen: v }))}>
                <SelectTrigger className="h-9 text-xs bg-white"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {sucursales.map((d) => <SelectItem key={d.nombre} value={d.nombre}>{d.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Sucursal destino</Label>
              <Select value={mergeDirForm.sucursal_destino} onValueChange={(v) => setMergeDirForm((p) => ({ ...p, sucursal_destino: v }))}>
                <SelectTrigger className="h-9 text-xs bg-white"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {sucursales.map((d) => <SelectItem key={d.nombre} value={d.nombre}>{d.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMergeDir(false)}>Cancelar</Button>
            <Button className="bg-[#384091] text-white" onClick={submitMergeDirecciones}>Fusionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EquiposTable({ rows, loading, selected, toggleSel, openDetail, fmtUbi, fmtDate, showPending }) {
  if (loading) return <p className="text-center text-gray-400 py-12">Cargando…</p>;
  if (!rows || rows.length === 0) return <p className="text-center text-gray-400 py-12">Sin equipos</p>;

  const sortedRows = showPending
    ? [...rows].sort((a, b) => String(a.proxima_mp_fecha || "9999").localeCompare(String(b.proxima_mp_fecha || "9999")))
    : rows;

  return (
    <>
      {/* Desktop table */}
      <div className="hidden lg:block">
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#F5F7FA] text-[#384091]">
                <tr>
                  {!showPending && <th className="p-2 w-8"></th>}
                  <th className="p-2 text-left">Cliente</th>
                  <th className="p-2 text-left">Sucursal</th>
                  <th className="p-2 text-left">Tipo</th>
                  <th className="p-2 text-left">Marca</th>
                  <th className="p-2 text-left">Modelo</th>
                  <th className="p-2 text-left">Serie</th>
                  <th className="p-2 text-left">Código</th>
                  <th className="p-2 text-left">Ubicación</th>
                  <th className="p-2 text-left">Última MP</th>
                  <th className="p-2 text-left">Próxima MP</th>
                  <th className="p-2 text-left">Estado</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((eq) => (
                  <tr key={eq.id} className="border-t hover:bg-[#0FB4B708] cursor-pointer" onClick={() => openDetail(eq)}>
                    {!showPending && (
                      <td className="p-2" onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={selected.includes(eq.id)} onCheckedChange={() => toggleSel(eq.id)} />
                      </td>
                    )}
                    <td className="p-2">{eq.cliente_nombre || "—"}</td>
                    <td className="p-2">{eq.sucursal || "—"}</td>
                    <td className="p-2">{eq.tipo_equipo || "—"}</td>
                    <td className="p-2">{eq.marca || "—"}</td>
                    <td className="p-2">{eq.modelo || "—"}</td>
                    <td className="p-2 font-mono text-[11px]">{eq.numero_serie || "—"}</td>
                    <td className="p-2 font-mono text-[11px]">{eq.codigo_interno || "—"}</td>
                    <td className="p-2">{fmtUbi(eq.ubicacion)}</td>
                    <td className="p-2">{fmtDate(eq.ultima_mp_fecha)}</td>
                    <td className="p-2">
                      <div className="flex items-center gap-1">
                        {fmtDate(eq.proxima_mp_fecha)}
                        {showPending && pendienteBadge(eq.motivo_pendiente)}
                      </div>
                    </td>
                    <td className="p-2">
                      <Badge className={eq.estado === "de_baja" ? "bg-gray-100 text-gray-600 border-0" : "bg-emerald-50 text-emerald-700 border-0"}>
                        {eq.estado === "de_baja" ? "De baja" : "Activo"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden space-y-2">
        {sortedRows.map((eq) => (
          <Card key={eq.id} className="border-0 shadow-sm hover:shadow-md cursor-pointer" onClick={() => openDetail(eq)}>
            <CardContent className="p-3">
              <div className="flex items-start gap-2">
                {!showPending && (
                  <div onClick={(e) => e.stopPropagation()} className="pt-0.5">
                    <Checkbox checked={selected.includes(eq.id)} onCheckedChange={() => toggleSel(eq.id)} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium truncate">{eq.marca} {eq.modelo}</p>
                    <Badge className={eq.estado === "de_baja" ? "bg-gray-100 text-gray-600 border-0" : "bg-emerald-50 text-emerald-700 border-0"}>
                      {eq.estado === "de_baja" ? "De baja" : "Activo"}
                    </Badge>
                  </div>
                  <p className="text-[11px] text-gray-500 truncate">{eq.cliente_nombre} · {eq.sucursal}</p>
                  <p className="text-[11px] text-gray-500 truncate">{eq.tipo_equipo} · {fmtUbi(eq.ubicacion)}</p>
                  <p className="text-[11px] text-gray-500 font-mono">Serie: {eq.numero_serie || "—"} · Cód: {eq.codigo_interno || "—"}</p>
                  <p className="text-[11px] text-gray-500">
                    Última MP: {fmtDate(eq.ultima_mp_fecha)} · Próx: {fmtDate(eq.proxima_mp_fecha)}
                    {showPending && <span className="ml-1">{pendienteBadge(eq.motivo_pendiente)}</span>}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}

function HistorialSection({ title, icon: Icon, items, onDownload }) {
  return (
    <div className="mb-3">
      <h4 className="text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
        <Icon className="h-3.5 w-3.5 text-[#0FB4B7]" /> {title} <span className="text-gray-400">({items.length})</span>
      </h4>
      {items.length === 0 ? (
        <p className="text-[11px] text-gray-400 pl-5">Sin registros</p>
      ) : (
        <ul className="space-y-1">
          {items.map((it) => (
            <li key={it.id} className="flex items-center justify-between gap-2 bg-gray-50 rounded px-2 py-1">
              <div className="min-w-0">
                <p className="text-xs font-medium truncate">{it.folio || it.id} {it.tipo ? <span className="text-gray-400">· {it.tipo}</span> : null}</p>
                <p className="text-[10px] text-gray-500">{it.fecha || ""}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => onDownload(it)} title="Descargar PDF">
                <Download className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
